import { CustomizeItemUIBase, ICustomizeItemUiParams } from "../base/customize-item-ui-base";
import { HalfModifierSelection, Modals } from "../../../../../types/enums";
import { Util } from "../../../../../utils/util";
import { EventTypes } from "../../../../../core/event-bus";
import { ISelectSecondHalfProps } from "./modals/select-second-half/select-second-half";
import { IDataItem } from "../../../../../models/data.interface";

export class CustomizeItemUIMultiItem extends CustomizeItemUIBase {
    constructor(params: ICustomizeItemUiParams) {
        super({
            modalId: "modal_customize_item",
            eventBus: params.eventBus,
            data: params.data,
            context: params.context,
            localizationService: params.localizationService,
            order: params.order,
            dataLayerService: params.dataLayerService,
            profileService: params.profileService,
            modalName: Modals.CUSTOMIZE_ITEM,
            core: params.core,
            favorite: params.favorite
        });
    }

    override _setEventListeners(): void {
        super._setEventListeners();
        Util.setElement("onclick", "btn_customize_item_half_half", this._handleHalfHalfClick);
        Util.setElement("onclick", "btn_customize_item_header_multi_first_swap", () => this._handleHalfHalfSwapClick(HalfModifierSelection.LEFT));
        Util.setElement("onclick", "btn_customize_item_header_multi_first_remove", () => this._handleHalfHalfRemoveClick(HalfModifierSelection.LEFT));
        Util.setElement("onclick", "btn_customize_item_header_multi_second_swap", () => this._handleHalfHalfSwapClick(HalfModifierSelection.RIGHT));
        Util.setElement("onclick", "btn_customize_item_header_multi_second_remove", () => this._handleHalfHalfRemoveClick(HalfModifierSelection.RIGHT));
    }

    override _eventBusListeners(): void {
        super._eventBusListeners();
        this._trackListener(EventTypes.SELECT_SECOND_HALF_SELECTED, this._handleHalfItemSelect);
    }

    override _renderPage(isFirstRender: boolean = false): void {
        if (this._modules.core.isItemHalfable())
            Util.showElement("div_customize_item_half_half");
        else
            Util.hideElement("div_customize_item_half_half");

        super._renderPage(isFirstRender);

        if (this._modules.core.isHalfMode) {
            this._renderHalfHalfHeaderSection(HalfModifierSelection.LEFT);
            this._renderHalfHalfHeaderSection(HalfModifierSelection.RIGHT);
        }
    }

    private readonly _handleHalfHalfClick = (): void => {
        this._modules.core.isHalfMode = true;
        super._renderPage();
        this._renderHalfHalfHeaderSection(HalfModifierSelection.LEFT);
        this._renderHalfHalfHeaderSection(HalfModifierSelection.RIGHT);
    }

    private readonly _handleHalfHalfSwapClick = (halfId: HalfModifierSelection): void => {
        const currentHalfId = halfId === HalfModifierSelection.LEFT ? HalfModifierSelection.RIGHT : HalfModifierSelection.LEFT;
        const props: ISelectSecondHalfProps = {
            halfId,
            sizeId: this._modules.core.sizeId,
            item: this._modules.core.getMenuItem(currentHalfId),
            mealDeal: this._modules.core.mealDeal
        };

        this._eventBus.emit(EventTypes.SELECT_SECOND_HALF_OPEN, props);
    }

    private readonly _handleHalfHalfRemoveClick = (halfId: HalfModifierSelection): void => {
        this._modules.core.isHalfMode = false;
        this._modules.core.cloneHalfState(halfId);
        this._renderPage();
    }

    protected override _hideAndShowPreModifierMultiButtonsCheckbox(modifierDisplayGroupId: number | null, modifierId: number, halfId: HalfModifierSelection, isChecked: boolean): void {
        if (!modifierDisplayGroupId)
            return;

        const action = isChecked ? Util.showElement : Util.hideElement;
        action(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}_${halfId}`);
    }

    protected override _hideAndShowPreModifierButtonsRadio(modifierDisplayGroupId: number | null, modifierId: number, halfId: HalfModifierSelection, isChecked: boolean): void {
        if (!modifierDisplayGroupId)
            return;

        let premodifierButtons = Array.from(document.querySelectorAll(`div[id^="div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_"]`)) as HTMLElement[];
        premodifierButtons = premodifierButtons.filter(x => x.id.endsWith(`_${halfId}`));
        for (const button of premodifierButtons) {
            Util.hideElement(button);
        }

        if (isChecked)
            Util.showElement(`div_customize_item_modifier_premodifier_selection_${modifierDisplayGroupId}_${modifierId}_${halfId}`);
    }

    protected override _appendHeader(_parent: HTMLElement): void {
        const halfableHeaderDiv = this._createHalfableHeader();
        _parent.appendChild(halfableHeaderDiv);
    }

    private _createHalfableHeader(): HTMLElement {
        const html = `
            <div style="display: flex; justify-content: flex-end;">
                <span class="icon-item-half-left" aria-hidden="true" style="margin-right: 3rem; font-size: 1.5rem;"></span>
                <span class="icon-item-half-right" aria-hidden="true" style="font-size: 1.5rem;"></span>
            </div>`;

        return Util.createHtmlElementFromTemplate(html);
    }

    private _renderHalfHalfHeaderSection(halfId: HalfModifierSelection): void {
        Util.hideElement("div_customize_item_image");

        Util.hideElement("div_customize_item_header_single");
        Util.showElement("div_customize_item_header_multi");
        this._setAriaLabelledBy("h1_customize_item_multi_name");

        const half = halfId === HalfModifierSelection.LEFT ? "first" : "second";
        const menuItem = this._modules.core.getMenuItem(halfId);

        if (menuItem.ImageURL) {
            Util.setElement("src", `img_customize_item_header_multi_${half}_image`, menuItem.ImageURL);
            Util.showElement(`div_customize_item_header_multi_${half}_image`);
        } else {
            Util.hideElement(`div_customize_item_header_multi_${half}_image`);
        }

        const itemName = this._localizationService.GetName(menuItem.Names);
        Util.setElement("alt", `img_customize_item_header_multi_${half}_image`, itemName);

        if (halfId === HalfModifierSelection.LEFT)
            Util.setElement("innerText", "h1_customize_item_header_multi_first_name", itemName);
        else
            Util.setElement("innerText", "h1_customize_item_header_multi_second_name", itemName);

        // TODO: Add calories and show
        // spn_customize_item_header_multi_${half}_calories
    }

    private readonly _handleHalfItemSelect = (menuItem: IDataItem, halfId: HalfModifierSelection): void => {
        this._modules.core.setMenuItem(halfId, menuItem);
        this._modules.core.resetHalfModifiersState(halfId);

        this._handleHalfHalfClick();
    }
}